// connectAsk — the words, and the gate, of the "ask netti how to connect it" door.
//
// The door is one soft field and one button at the foot of the connections lists.
// What it sends is the ask a person would type themselves: what they want to
// connect, and the three ways innernet can be reached (the mcp link, npx
// innernet, the api) laid out for netti to choose between. The ask reads as
// connecting ("npx innernet" is a connect word to netti's reader), so netti hands
// the turn its connect hands: the guide for that tool, the surface steps, and the
// web when nothing is recorded about the app.
//
// Kept apart from the component so the words and the gate are tested plainly.

import { canSee, F, type Viewer } from "../../lib/features/registry";

/** the longest thing a person can name here ("the notes app my team uses on tuesdays") */
export const WHAT_MAX = 80;

/** what they typed, as one tidy phrase: control characters out, space folded, a trailing full stop or question mark dropped, cut at a word */
export function cleanWhat(raw: unknown): string {
  if (typeof raw !== "string") return "";
  // eslint-disable-next-line no-control-regex
  const flat = raw.replace(/[\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim().replace(/[.?!,;:\s]+$/, "");
  if (flat.length <= WHAT_MAX) return flat;
  const cut = flat.slice(0, WHAT_MAX);
  const at = cut.lastIndexOf(" ");
  return (at > WHAT_MAX * 0.5 ? cut.slice(0, at) : cut).trim();
}

const WAYS = "the mcp connector, npx innernet, or the api";

/**
 * The ask netti is sent, in the person's voice. With a name it asks which way fits
 * that app; with none it says it is not sure what it wants yet, and lets netti ask.
 * The wording keeps clear of netti's order words (it is a question, never work on
 * the memory), and asks for the lookup outright so netti does not stop at what it
 * already knows about an app it has never seen.
 */
export function composeConnectAsk(raw: unknown): string {
  const what = cleanWhat(raw);
  return what
    ? `i want to connect ${what} to innernet. which way fits best: ${WAYS}? look it up if you need to, and walk me through it.`
    : `i want to connect something to innernet and i'm not sure which way fits: ${WAYS}. ask me what it is, look it up if you need to, and walk me through it.`;
}

/**
 * Is netti's conversation open to this person? Founders, until
 * NEXT_PUBLIC_NETTI_CHAT_OPEN=1 opens it to everyone (the same switch the dock,
 * the ask route and the hands door read). With no viewer given the answer is
 * yes: the dock itself says so honestly when it is not public yet.
 */
export function nettiIsHere(viewer?: Viewer, open: boolean = process.env.NEXT_PUBLIC_NETTI_CHAT_OPEN === "1"): boolean {
  if (!viewer) return true;
  return open || canSee(F.nettiChat, viewer);
}
